import { inject } from '@angular/core';
import { Column } from 'angular-slickgrid';
import { BaseCrudComponent, FieldMapperHelper, ICrudService } from '@zendra/shared';
import {
  GetGisStandInconsistenciesInputDto,
  GisStandInconsistenciesExcelDownloadDto,
  GisStandInconsistencyDto,
  GisStandInconsistencyPlanningOrchestratorService,
} from 'orchestrator';
import { map, Observable, throwError } from 'rxjs';

export type GisStandInconsistencyRow = GisStandInconsistencyDto & { id: number };

type UnsupportedCrudDto = Record<string, never>;

export abstract class GisStandInconsistencyBase extends BaseCrudComponent<
  GisStandInconsistencyRow,
  UnsupportedCrudDto,
  UnsupportedCrudDto,
  GisStandInconsistenciesExcelDownloadDto
> {
  private readonly orchestratorService = inject(GisStandInconsistencyPlanningOrchestratorService);

  protected abstract get caseFilter(): Partial<GetGisStandInconsistenciesInputDto>;

  private cachedColumns: Column[] | null = null;

  get columns(): Column[] {
    this.cachedColumns ??= this.buildColumnsWithoutForeignKeyIdColumns();

    return this.cachedColumns;
  }

  service: ICrudService<
    GisStandInconsistencyRow,
    UnsupportedCrudDto,
    UnsupportedCrudDto,
    GisStandInconsistenciesExcelDownloadDto
  > = {
    create: (_input: UnsupportedCrudDto) => this.unsupportedCrudOperation('create'),
    update: (_id: number, _input: UnsupportedCrudDto) => this.unsupportedCrudOperation('update'),
    deleteByIds: (_ids: number[]) => this.unsupportedCrudOperation('delete'),
    getList: query =>
      this.orchestratorService
        .getList({ ...query, ...this.caseFilter } as GetGisStandInconsistenciesInputDto)
        .pipe(
          map(response => ({
            items: this.withSyntheticIds(response.items ?? [], query?.skipCount ?? 0),
            totalCount: response.totalCount ?? 0,
          })),
        ),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.orchestratorService.getListAsExcelFile({ ...input, ...this.caseFilter }),
  };

  protected prepareUpdateDto(_data: any, _selected: GisStandInconsistencyRow): UnsupportedCrudDto {
    return {};
  }

  private withSyntheticIds(
    items: GisStandInconsistencyDto[],
    skipCount: number,
  ): GisStandInconsistencyRow[] {
    return items.map((item, index) => ({
      ...item,
      id: skipCount + index + 1,
    }));
  }

  private buildColumnsWithoutForeignKeyIdColumns(): Column[] {
    const foreignKeyIdColumnIds = new Set(
      this.fields.flatMap(field =>
        (field.additionalGridColumns ?? []).map(additionalColumn => additionalColumn.key),
      ),
    );

    return FieldMapperHelper.buildColumnsFromFields(
      this.fields,
      this.localizationService,
      this.injector,
    ).filter(column => !foreignKeyIdColumnIds.has(String(column.id)));
  }

  private unsupportedCrudOperation<T>(operation: string): Observable<T> {
    return throwError(
      () =>
        new Error(
          `GIS stand inconsistencies are a read-only report maintained by the synchronization job, so ${operation} is not supported.`,
        ),
    );
  }
}